import React from 'react';
import { Typography } from '@mui/material';
import { TableColumnIdEnum } from '../../constants';
import { EstimationComparisonTableRow } from '../../models';

const NumberToFixed = 2;

const formatNumber = (val: number) => {
  if (val < 0) {
    return <Typography component="span" variant="body2" color="red">{`(${Math.abs((val || 0)).toFixed(NumberToFixed)})`}</Typography>;
  }
  return <Typography component="span" variant="body2">{(val || 0).toFixed(NumberToFixed)}</Typography>;
};

export const estimationComparisonTableColumns = {
  header: [{
    columnId: TableColumnIdEnum.Month,
    label: 'Month',
    align: 'center',
    type: 'header',
    colSpan: 1,
  },
  {
    columnId: TableColumnIdEnum.BillStartDate,
    label: 'Bill Start Date',
    align: 'center',
    type: 'header',
    colSpan: 1,
  },
  {
    columnId: TableColumnIdEnum.BillEndDate,
    label: 'Bill End Date',
    align: 'center',
    type: 'header',
    colSpan: 1,
  },
  {
    columnId: TableColumnIdEnum.BillDays,
    label: 'Total # Days',
    align: 'center',
    type: 'header',
    colSpan: 1,
  },
  {
    columnId: TableColumnIdEnum.OnPeakUsage,
    label: 'On Peak Usage',
    align: 'center',
    type: 'header',
    colSpan: 2,
  },
  {
    columnId: TableColumnIdEnum.MidPeakUsage,
    label: 'Mid Peak Usage',
    align: 'center',
    type: 'header',
    colSpan: 2,
  },
  {
    columnId: TableColumnIdEnum.OffPeakUsage,
    label: 'Off Peak Usage',
    align: 'center',
    type: 'header',
    colSpan: 2,
  },
  {
    columnId: TableColumnIdEnum.TotalUsage,
    label: 'Total Usage',
    align: 'center',
    type: 'header',
    colSpan: 2,
  },
  {
    columnId: TableColumnIdEnum.OnPeakCost,
    label: 'On Peak Cost',
    align: 'center',
    type: 'header',
    colSpan: 2,
  },
  {
    columnId: TableColumnIdEnum.MidPeakCost,
    label: 'Mid Peak Cost',
    align: 'center',
    type: 'header',
    colSpan: 2,
  },
  {
    columnId: TableColumnIdEnum.OffPeakCost,
    label: 'Off Peak Cost',
    align: 'center',
    type: 'header',
    colSpan: 2,
  },
  {
    columnId: TableColumnIdEnum.WinterStep1Cost,
    label: 'Step 1 Cost (Winter)',
    align: 'center',
    type: 'header',
    colSpan: 2,
  },
  {
    columnId: TableColumnIdEnum.SummerStep1Cost,
    label: 'Step 1 Cost (Summer)',
    align: 'center',
    type: 'header',
    colSpan: 2,
  },
  {
    columnId: TableColumnIdEnum.WinterStep2Cost,
    label: 'Step 2 Cost (Winter)',
    align: 'center',
    type: 'header',
    colSpan: 2,
  },
  {
    columnId: TableColumnIdEnum.SummerStep2Cost,
    label: 'Step 2 Cost (Summer)',
    align: 'center',
    type: 'header',
    colSpan: 2,
  },
  {
    columnId: TableColumnIdEnum.WinterStep3Cost,
    label: 'Step 3 Cost (Winter)',
    align: 'center',
    type: 'header',
    colSpan: 2,
  },
  {
    columnId: TableColumnIdEnum.SummerStep3Cost,
    label: 'Step 3 Cost (Summer)',
    align: 'center',
    type: 'header',
    colSpan: 2,
  },
  {
    columnId: TableColumnIdEnum.TotalEnergyCost,
    label: 'Total Energy Cost',
    align: 'center',
    type: 'header',
    colSpan: 2,
  },
  {
    columnId: TableColumnIdEnum.BasicServiceCost,
    label: 'Basic Service Cost / Customer Charge',
    align: 'center',
    type: 'header',
    colSpan: 2,
  },
  {
    columnId: TableColumnIdEnum.OnPeakDemandCost,
    label: 'On-Peak Demand Cost',
    align: 'center',
    type: 'header',
    colSpan: 2,
  },
  {
    columnId: TableColumnIdEnum.CustomerDemandCost,
    label: 'Customer Demand Cost',
    align: 'center',
    type: 'header',
    colSpan: 2,
  },
  {
    columnId: TableColumnIdEnum.FuelAdjustmentCharge,
    label: 'Fuel Adjustment Charge',
    align: 'center',
    type: 'header',
    colSpan: 1,
  },
  {
    columnId: TableColumnIdEnum.Total,
    label: 'Total',
    align: 'center',
    type: 'header',
    colSpan: 2,
  },
  {
    columnId: TableColumnIdEnum.TotalDiff,
    label: 'Difference',
    align: 'center',
    type: 'header',
    colSpan: 2,
  },
  {
    columnId: TableColumnIdEnum.TotalBilled,
    label: 'Total Billed (including all taxes and other fees)',
    align: 'center',
    type: 'header',
    colSpan: 1,
  },
  {
    columnId: TableColumnIdEnum.Ignored,
    label: 'Ignored',
    align: 'center',
    type: 'header',
    colSpan: 2,
  },
  ],
  subHeader: [
    {
      columnId: TableColumnIdEnum.Month,
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.BillStartDate,
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.BillEndDate,
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.BillDays,
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.OnPeakUsage,
      label: 'Billed (kWh)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.OnPeakUsage,
      label: 'Calculated (kWh)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.MidPeakUsage,
      label: 'Billed (kWh)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.MidPeakUsage,
      label: 'Calculated (kWh)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.OffPeakUsage,
      label: 'Billed (kWh)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.OffPeakUsage,
      label: 'Calculated (kWh)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.TotalUsage,
      label: 'Billed (kWh)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.TotalUsage,
      label: 'Calculated (kWh)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.OnPeakCost,
      label: 'Billed ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.OnPeakCost,
      label: 'Calculated ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.MidPeakCost,
      label: 'Billed ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.MidPeakCost,
      label: 'Calculated ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.OffPeakCost,
      label: 'Billed ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.OffPeakCost,
      label: 'Calculated ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.WinterStep1Cost,
      label: 'Billed ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.WinterStep1Cost,
      label: 'Calculated ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.SummerStep1Cost,
      label: 'Billed ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.SummerStep1Cost,
      label: 'Calculated ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.WinterStep2Cost,
      label: 'Billed ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.WinterStep2Cost,
      label: 'Calculated ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.SummerStep2Cost,
      label: 'Billed ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.SummerStep2Cost,
      label: 'Calculated ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.WinterStep3Cost,
      label: 'Billed ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.WinterStep3Cost,
      label: 'Calculated ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.SummerStep3Cost,
      label: 'Billed ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.SummerStep3Cost,
      label: 'Calculated ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.TotalEnergyCost,
      label: 'Billed ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.TotalEnergyCost,
      label: 'Calculated ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.BasicServiceCost,
      label: 'Billed ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.BasicServiceCost,
      label: 'Calculated ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.OnPeakDemandCost,
      label: 'Billed ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.OnPeakDemandCost,
      label: 'Calculated ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.CustomerDemandCost,
      label: 'Billed ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.CustomerDemandCost,
      label: 'Calculated ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.FuelAdjustmentCharge,
      label: 'Calculated ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.Total,
      label: 'Billed ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.Total,
      label: 'Calculated ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.TotalDiff,
      label: 'Total ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.TotalDiff,
      label: 'Percent (%)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.TotalBilled,
      label: '($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.Ignored,
      label: 'Difference ($)',
      align: 'center',
      type: 'subHeader',
    },
    {
      columnId: TableColumnIdEnum.Ignored,
      label: 'Percent (%)',
      align: 'center',
      type: 'subHeader',
    },
  ],
  body: [{
    columnId: TableColumnIdEnum.Month,
    label: (data: EstimationComparisonTableRow) => (
      <Typography
        color={data.isMissingMonth ? 'red' : 'unset'}
        variant="body2"
        title={
          data.isMissingMonth ? 'This month\'s data is excluded from total calculations due to its absence in the original dataset.' : ''
        }
      >
        {data.month}
        {
          data.isMissingMonth
            && (
            <span>
              {' ⓘ'}
            </span>
            )
        }
      </Typography>
    ),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.BillStartDate,
    label: (data: EstimationComparisonTableRow) => data.fromDate.toLocaleDateString(),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.BillEndDate,
    label: (data: EstimationComparisonTableRow) => data.toDate.toLocaleDateString(),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.BillDays,
    label: (data: EstimationComparisonTableRow) => `${data.billedDays}`,
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.OnPeakUsage,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billOnPeakUsage),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.OnPeakUsage,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.onPeakUsage),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.MidPeakUsage,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billMidPeakUsage),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.MidPeakUsage,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.midPeakUsage),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.OffPeakUsage,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billOffPeakUsage),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.OffPeakUsage,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.offPeakUsage),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.TotalUsage,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billTotalUsage),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.TotalUsage,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.totalUsage),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.OnPeakCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billOnPeakCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.OnPeakCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.onPeakCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.MidPeakCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billMidPeakCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.MidPeakCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.midPeakCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.OffPeakCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billOffPeakCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.OffPeakCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.offPeakCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.WinterStep1Cost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billWinter1StepCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.WinterStep1Cost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.winter1StepCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.SummerStep1Cost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billSummer1StepCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.SummerStep1Cost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.summer1StepCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.WinterStep2Cost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billWinter2StepCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.WinterStep2Cost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.winter2StepCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.SummerStep2Cost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billSummer2StepCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.SummerStep2Cost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.summer2StepCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.WinterStep3Cost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billWinter3StepCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.WinterStep3Cost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.winter3StepCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.SummerStep3Cost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billSummer3StepCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.SummerStep3Cost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.summer3StepCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.TotalEnergyCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billTotalEnergyCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.TotalEnergyCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.totalEnergyCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.BasicServiceCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billTotalServiceCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.BasicServiceCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.totalServiceCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.OnPeakDemandCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billOnPeakDemandCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.OnPeakDemandCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.onPeakDemandCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.CustomerDemandCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billCustomerDemandCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.CustomerDemandCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.customerDemandCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.FuelAdjustmentCharge,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.fuelAdjustmentCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.Total,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billTotalCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.Total,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.totalCharge),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.TotalDiff,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.totalDiff),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.TotalDiff,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.totalDiffPercentage),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.TotalBilled,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billTotal),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.Ignored,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.ignored),
    align: 'center',
    type: 'data',
  },
  {
    columnId: TableColumnIdEnum.Ignored,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.ignoredPercentage),
    align: 'center',
    type: 'data',
  },
  ],
  total: [{
    columnId: TableColumnIdEnum.Month,
    label: 'Total',
    align: 'left',
    type: 'total',
    colSpan: 3,
  },
  {
    columnId: TableColumnIdEnum.BillDays,
    label: (data: EstimationComparisonTableRow) => `${data.billedDays}`,
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.OnPeakUsage,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billOnPeakUsage),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.OnPeakUsage,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.onPeakUsage),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.MidPeakUsage,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billMidPeakUsage),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.MidPeakUsage,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.midPeakUsage),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.OffPeakUsage,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billOffPeakUsage),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.OffPeakUsage,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.offPeakUsage),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.TotalUsage,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billTotalUsage),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.TotalUsage,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.totalUsage),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.OnPeakCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billOnPeakCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.OnPeakCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.onPeakCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.MidPeakCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billMidPeakCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.MidPeakCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.midPeakCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.OffPeakCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billOffPeakCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.OffPeakCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.offPeakCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.WinterStep1Cost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billWinter1StepCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.WinterStep1Cost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.winter1StepCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.SummerStep1Cost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billSummer1StepCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.SummerStep1Cost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.summer1StepCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.WinterStep2Cost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billWinter2StepCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.WinterStep2Cost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.winter2StepCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.SummerStep2Cost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billSummer2StepCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.SummerStep2Cost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.summer2StepCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.WinterStep3Cost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billWinter3StepCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.WinterStep3Cost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.winter3StepCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.SummerStep3Cost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billSummer3StepCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.SummerStep3Cost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.summer3StepCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.TotalEnergyCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billTotalEnergyCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.TotalEnergyCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.totalEnergyCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.BasicServiceCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billTotalServiceCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.BasicServiceCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.totalServiceCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.OnPeakDemandCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billOnPeakDemandCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.OnPeakDemandCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.onPeakDemandCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.CustomerDemandCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billCustomerDemandCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.CustomerDemandCost,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.customerDemandCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.FuelAdjustmentCharge,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.fuelAdjustmentCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.Total,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billTotalCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.Total,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.totalCharge),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.TotalDiff,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.totalDiff),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.TotalDiff,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.totalDiffPercentage),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.TotalBilled,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.billTotal),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.Ignored,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.ignored),
    align: 'center',
    type: 'total',
  },
  {
    columnId: TableColumnIdEnum.Ignored,
    label: (data: EstimationComparisonTableRow) => formatNumber(data.ignoredPercentage),
    align: 'center',
    type: 'total',
  },
  ],
} as Record<'header' | 'subHeader' | 'body' | 'total', {
  columnId: TableColumnIdEnum;
  label: ((data: EstimationComparisonTableRow) => JSX.Element) | string | undefined;
  align: 'center' | 'left';
  type: 'header' | 'subHeader' | 'data' | 'total';
  colSpan?: number;
}[]>;
